import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { AppHTTPService } from '../api/http-service'

declare global {
  interface Window {
    Pylon: any
  }
}

interface PylonContextType {
  configure: (actor: { account_id: string; email: string; name: string }) => Promise<void>
  showNewMessage: () => void
}

const PylonContext = createContext<PylonContextType>({
  configure: async () => {},
  showNewMessage: () => {},
})

const PylonProvider = ({ children }: { children: React.ReactNode }) => {
  const pylonAppId = window.flatfile.configService.get('VITE_PYLON_APP_ID')
  const pylonLoaded = useRef(false)
  const isProduction = useMemo(() => process.env.NODE_ENV === 'production', [])
  const renderPylon = useMemo(() => pylonAppId && isProduction, [pylonAppId, isProduction])

  useEffect(() => {
    if (renderPylon && !pylonLoaded.current) {
      initialize()
    }
  }, [renderPylon])

  /**
   * Embeddable script converted to function for easier control over injection based on entitlement
   * https://docs.usepylon.com/pylon-docs/in-app-chat/chat-setup
   */
  const initialize = () => {
    if (pylonLoaded.current) {
      return
    }
    pylonLoaded.current = true
    var e = window as any
    var t = document
    var n: any = function () {
      n.e(arguments)
    }
    n.q = []
    n.e = function (e: any) {
      n.q.push(e)
    }
    e.Pylon = n
    var r = function () {
      var e = t.createElement('script')
      e.setAttribute('type', 'text/javascript')
      e.setAttribute('async', 'true')
      e.setAttribute('src', `https://widget.usepylon.com/widget/APP_ID`)
      var n = t.getElementsByTagName('script')[0]
      if (n && n.parentNode) {
        n.parentNode.insertBefore(e, n)
      } else {
        document.body.appendChild(e)
      }
    }

    if (t.readyState === 'complete') {
      r()
    } else if (e.addEventListener) {
      e.addEventListener('load', r, false)
    }
  }

  const configure = async (actor: { account_id: string; email: string; name: string }) => {
    if (renderPylon) {
      let emailHash: string | undefined
      try {
        const { data } = await AppHTTPService.axios.get('/v1/auth/pylon/identity-hash')
        emailHash = data?.data?.emailHash
      } catch (error) {
        console.warn('Failed to fetch Pylon identity hash, continuing without verification:', error)
      }

      ;(window as any).pylon = {
        chat_settings: {
          account_id: actor.account_id,
          app_id: pylonAppId,
          email: actor.email,
          name: actor.name,
          ...(emailHash && { email_hash: emailHash }),
        },
      }
    }
  }

  const showNewMessage = useCallback(() => {
    // Pylon API for invoking the chat widget https://docs.usepylon.com/pylon-docs/in-app-chat/javascript-api#window-pylon-shownewmessage-string
    window.Pylon('showNewMessage', '')
  }, [])

  return <PylonContext.Provider value={{ configure, showNewMessage }}>{children}</PylonContext.Provider>
}

const usePylon = () => {
  const context = useContext(PylonContext)
  if (!context) {
    throw new Error('usePylon must be used within a PylonProvider')
  }

  return context
}

export { PylonProvider, usePylon }
